import { useQuery } from "@tanstack/react-query";
import type { DeliveryPace, PaceRow } from "@aura/shared";
import { Figure, Ledger } from "~/components/charts";
import { ed, tbl } from "~/components/editorial";
import { api } from "~/lib/api";
import { fmtInt, fmtPct } from "~/lib/format";
import { CumulativeLines } from "./charts";
import { useBotsParam } from "./lib";
import { BotsToggle, Byline, Controls, ReportShell, SectionHead } from "./parts";
import { paceDek, paceHeadline, signed } from "./story";

function PaceTable({
  rows,
  nameHead,
  pace,
  total,
}: {
  rows: PaceRow[];
  nameHead: string;
  pace: DeliveryPace;
  total?: PaceRow;
}) {
  const q = pace.quarter.short;
  const last = pace.lastQuarter.short;
  return (
    <div className={tbl.scroll}>
      <table className={tbl.numeric}>
        <thead>
          <tr>
            <th>{nameHead}</th>
            <th data-num>{q} so far</th>
            <th data-num>{last} by day {pace.quarter.days}</th>
            <th data-num>Change</th>
            <th data-num>Checked {q}</th>
            <th data-num>{q} projected</th>
            <th data-num>{last} total</th>
            <th data-num>Projected vs {last}</th>
          </tr>
        </thead>
        <tbody>
          {[...rows, ...(total ? [total] : [])].map((r) => {
            const vs = r.projected !== null && r.lastTotal > 0 ? Math.round((100 * (r.projected - r.lastTotal)) / r.lastTotal) : null;
            return (
              <tr key={r.key} data-total={r.key === "pxe" || undefined}>
                <td data-text style={{ fontWeight: r.key === "pxe" ? 700 : 600, whiteSpace: "nowrap" }}>
                  {r.label}
                </td>
                <td data-num>{fmtInt(r.toDate.merged)}</td>
                <td data-num data-dim>
                  {fmtInt(r.lastToDate.merged)}
                </td>
                <td data-num>{signed(r.toDate.merged - r.lastToDate.merged)}</td>
                <td data-num>{fmtPct(r.toDate.checkedPct)}</td>
                <td data-num>{r.projected === null ? "–" : fmtInt(r.projected)}</td>
                <td data-num data-dim>
                  {fmtInt(r.lastTotal)}
                </td>
                <td data-num>{vs === null ? "–" : `${vs > 0 ? "+" : ""}${vs}%`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// How this quarter is pacing against the same days of the last two.
export function PacePage() {
  const { includeBots, setIncludeBots } = useBotsParam();
  const { data, isLoading } = useQuery({
    queryKey: ["delivery-pace", includeBots],
    queryFn: () => api.get<{ data: DeliveryPace }>(`/delivery/pace${includeBots ? "?bots=1" : ""}`),
    staleTime: 5 * 60 * 1000,
  });
  const p = data?.data;

  return (
    <ReportShell meta={p ? `${p.quarter.label}, day ${p.quarter.days} of ${p.quarter.totalDays}` : undefined}>
      <Controls>
        <BotsToggle includeBots={includeBots} onChange={setIncludeBots} />
      </Controls>
      {isLoading ? (
        <div className={ed.loading}>Loading the quarter…</div>
      ) : !p ? (
        <div className={ed.loading}>Couldn't load the quarter.</div>
      ) : (
        <>
          <div className={ed.kicker}>Engineering delivery · {p.quarter.label} pace</div>
          <h1 className={ed.headlineCompact}>{paceHeadline(p)}</h1>
          <p className={ed.dek}>{paceDek(p)}</p>
          <Byline title="Pace" meta={p} />
          <Ledger
            columns={4}
            hideMissingPrior
            items={[
              { label: `${p.quarter.short} so far`, value: fmtInt(p.pxe.toDate.merged), note: `${fmtPct(p.pxe.toDate.checkedPct)} checked` },
              { label: `${p.lastQuarter.short} by day ${p.quarter.days}`, value: fmtInt(p.pxe.lastToDate.merged), note: `${fmtPct(p.pxe.lastToDate.checkedPct)} checked` },
              { label: `${p.quarter.short} projected`, value: p.pxe.projected === null ? "–" : fmtInt(p.pxe.projected), note: p.tooEarly ? "too early to project" : "straight line from today" },
              { label: `${p.lastQuarter.short} total`, value: fmtInt(p.pxe.lastTotal) },
            ]}
          />

          <div className={ed.sect}>I. The running total</div>
          <Figure
            title={`Merged PxE PRs, running total by day of quarter`}
            sub={`${p.quarterLabels.current} against ${p.quarterLabels.last}${p.cumulative.some((c) => c.twoBack !== null) ? ` and ${p.quarterLabels.twoBack}` : ""}. Across: day of quarter.`}
            method={`Day 1 is the first day of each quarter (UTC). A quarter that starts before Aura's history is left off. The projection is straight-line: ${p.quarter.short} so far divided by days elapsed, times the days in the quarter.`}
          >
            <CumulativeLines pace={p} />
          </Figure>

          <div className={ed.sect}>II. By product</div>
          <SectionHead title="Pace by product" />
          <PaceTable rows={p.byProduct} nameHead="Product" pace={p} total={p.pxe} />

          <div className={ed.sect}>III. By org</div>
          <SectionHead title="Pace by GitHub org" />
          <PaceTable rows={p.byOrg} nameHead="Org" pace={p} />
          <p className={ed.methodNarrow}>
            The last product row is the PxE total, which leaves out Outside PxE. Org rows include
            every repo.
          </p>
        </>
      )}
    </ReportShell>
  );
}
